import React, { useState } from 'react';
import { Message, Language } from '../types';
import MarkdownMessage from './MarkdownMessage';
import { FileText, ExternalLink } from 'lucide-react';

interface ChatMessageProps {
  message: Message;
  language: Language;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, language }) => {
  const [showActions, setShowActions] = useState(false);
  const [copied, setCopied] = useState(false);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleLike = () => {
    // TODO: Implement like functionality
    console.log('Liked message:', message.id);
  };

  return (
    <div 
      className={`flex ${message.isUserMessage ? 'justify-end' : 'justify-start'} px-1 sm:px-0 group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div
        className={`max-w-[90%] sm:max-w-sm lg:max-w-2xl px-2 py-2 sm:px-4 rounded-lg relative ${
          message.isUserMessage
            ? 'bg-primary text-white'
            : 'bg-white border border-gray-200 text-gray-900'
        }`}
      >
        {message.isUserMessage ? (
          <div className="text-sm leading-relaxed">{message.content}</div>
        ) : (
          <>
            <MarkdownMessage 
              content={message.content} 
              className="text-sm leading-relaxed"
            />
            
            {/* Relevant documents */}
            {message.relevantDocuments && message.relevantDocuments.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center text-xs text-gray-500 mb-2">
                  <FileText className="h-3 w-3 mr-1" />
                  Documents pertinents utilisés :
                </div>
                <div className="space-y-1">
                  {message.relevantDocuments.map((doc, index) => (
                    <div key={index} className="text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                      <div className="font-medium">{doc.documentName}</div>
                      <div className="text-gray-500 truncate">
                        {doc.content.substring(0, 100)}...
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        
        <div className="flex items-center justify-between mt-1">
          <div
            className={`text-xs ${
              message.isUserMessage ? 'text-blue-100' : 'text-gray-500'
            }`}
          >
            {formatTime(message.createdAt)}
          </div>
          
          {/* Action buttons - only for assistant messages */}
          {!message.isUserMessage && showActions && (
            <div className="flex items-center space-x-1 ml-2">
              <button
                onClick={handleCopy}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
                title={copied ? 'Copié !' : 'Copier'}
              >
                {copied ? (
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
              <button
                onClick={handleLike}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
                title="Utile"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
